import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Bell,
  Boxes,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  FileBarChart,
  History,
  Laptop,
  ShieldCheck,
  Tags,
  Users,
  Wrench,
} from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { ShortcutCard, StatCard } from "@/components/stat-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { SOP_VERSION } from "@/config/app";
import { apiFetch } from "@/lib/api";
import { useMe } from "@/lib/auth";
import type { AssetRow, AuditRow, ReminderRow, RequestRow } from "@/lib/master-data";
import { formatDateTime, todayLabel } from "@/lib/utils";

interface Health {
  status: string;
  version: string;
  now: string;
}

export function DashboardPage() {
  const { data: me } = useMe();
  const navigate = useNavigate();
  const canApprove = Boolean(me?.canApproveRequests);
  const canAudit = Boolean(me?.permissions.includes("audit:read"));

  const health = useQuery({
    queryKey: ["health"],
    queryFn: async (): Promise<Health> => {
      const response = await fetch("/api/v1/health");
      if (!response.ok) throw new Error("健康检查失败");
      const body = (await response.json()) as { data?: Health } & Health;
      return body.data ?? body;
    },
    refetchInterval: 60_000,
  });
  const approvals = useQuery({
    queryKey: ["dashboard-approvals"],
    queryFn: () => apiFetch<RequestRow[]>("/api/v1/requests?scope=approvals"),
    enabled: Boolean(me && canApprove),
  });
  const handovers = useQuery({
    queryKey: ["dashboard-handovers"],
    queryFn: () => apiFetch<RequestRow[]>("/api/v1/requests?scope=handovers"),
    enabled: Boolean(me),
  });
  const assets = useQuery({
    queryKey: ["dashboard-assets"],
    queryFn: () => apiFetch<AssetRow[]>("/api/v1/assets"),
    enabled: Boolean(me),
  });
  const reminders = useQuery({
    queryKey: ["dashboard-reminders"],
    queryFn: () =>
      apiFetch<{
        items: ReminderRow[];
        counts: { total: number; overdue: number; dueSoon: number };
      }>("/api/v1/reminders"),
    enabled: Boolean(me),
  });
  const audit = useQuery({
    queryKey: ["dashboard-audit"],
    queryFn: () => apiFetch<{ items: AuditRow[] }>("/api/v1/audit?page=1&pageSize=5"),
    enabled: Boolean(me && canAudit),
  });

  if (!me) return null;
  const pendingApprovals = approvals.data?.length ?? 0;
  const pendingHandovers = handovers.data?.length ?? 0;
  const assetCount = assets.data?.length ?? 0;
  const reminderCount = reminders.data?.counts.total ?? 0;
  const recentAudit = audit.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`你好，${me.user.displayName}`}
        description={`${todayLabel()} · 这是你的设备管理工作台。`}
        actions={
          <Button onClick={() => navigate({ to: "/requests" })}>
            <ClipboardList className="h-4 w-4" />
            发起申请
          </Button>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="待我审批"
          value={canApprove ? pendingApprovals : "—"}
          hint={canApprove ? "进入审批中心处理" : "未授予审批职责"}
          icon={ClipboardCheck}
        />
        <StatCard
          label="待我确认"
          value={pendingHandovers}
          hint="交接/归还确认"
          icon={ClipboardList}
          accent="warning"
        />
        <StatCard
          label="资产台账"
          value={assetCount}
          hint="当前可见范围"
          icon={Laptop}
          accent="success"
        />
        <StatCard
          label="待处理提醒"
          value={reminderCount}
          hint={`逾期 ${reminders.data?.counts.overdue ?? 0} · 即将到期 ${reminders.data?.counts.dueSoon ?? 0}`}
          icon={CalendarClock}
          accent="danger"
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_1fr]">
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>我的待办</CardTitle>
              <CardDescription>审批、签收与办理事项集中在这里。</CardDescription>
            </div>
            <Badge variant="secondary">{pendingApprovals + pendingHandovers} 项</Badge>
          </CardHeader>
          <CardContent>
            {pendingApprovals + pendingHandovers === 0 ? (
              <EmptyState
                title="当前没有待处理事项"
                description="有新的审批或交接确认时会出现在这里。"
                action={{ label: "发起申请", to: "/requests" }}
              />
            ) : (
              <div className="space-y-2">
                {pendingApprovals > 0 ? (
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => navigate({ to: "/approvals" })}
                  >
                    <span>待我审批 {pendingApprovals} 项</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : null}
                {pendingHandovers > 0 ? (
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => navigate({ to: "/requests" })}
                  >
                    <span>待我确认 {pendingHandovers} 项</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>系统状态</CardTitle>
            <CardDescription>当前部署与账号状态。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`h-2 w-2 rounded-full ${health.isSuccess ? "bg-emerald-500" : "bg-amber-500"}`}
                />
                API 服务
              </div>
              <Badge variant={health.isSuccess ? "success" : "warning"}>
                {health.isSuccess ? "正常" : health.isLoading ? "检查中" : "异常"}
              </Badge>
            </div>
            <Separator />
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">登录账号</span>
              <span className="max-w-56 truncate font-medium">
                {me.user.identifiers[0]?.value ?? "—"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">角色</span>
              <span className="max-w-56 truncate font-medium">
                {me.user.roles.map((role) => role.name).join("、") || "无"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">权限项</span>
              <span className="font-medium">{me.permissions.length}</span>
            </div>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground">最近检查</span>
              <span className="font-medium">{formatDateTime(health.data?.now)}</span>
            </div>
            <div className="rounded-lg bg-muted/70 p-3 text-xs leading-5 text-muted-foreground">
              资产、申请审批、交接确认、库存、软件许可、标签、维修、审计与附件已接入。SOP 版本{" "}
              {SOP_VERSION}。
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.35fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>快速开始</CardTitle>
            <CardDescription>常用业务入口。</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <ShortcutCard
              title="资产台账"
              description="入库、领用、借用、归还、维修与处置。"
              icon={Laptop}
              onClick={() => navigate({ to: "/assets" })}
            />
            <ShortcutCard
              title="业务办理"
              description="发起申请、跟踪审批与交接确认。"
              icon={ClipboardList}
              onClick={() => navigate({ to: "/requests" })}
            />
            <ShortcutCard
              title="库存视图"
              description="资产、低值易耗、耗材、软件许可。"
              icon={Boxes}
              onClick={() => navigate({ to: "/inventory" })}
            />
            <ShortcutCard
              title="标签任务"
              description="P900 标签预览、批量打印与确认。"
              icon={Tags}
              onClick={() => navigate({ to: "/label-tasks" })}
            />
            <ShortcutCard
              title="组织与人员"
              description="公司、部门、人员与账号匹配。"
              icon={Users}
              onClick={() => navigate({ to: "/settings/organization" })}
            />
            <ShortcutCard
              title="用户与角色"
              description="角色权限、数据范围与审批职责。"
              icon={ShieldCheck}
              onClick={() => navigate({ to: "/settings/access" })}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>最近动态</CardTitle>
            <CardDescription>
              {canAudit ? "最近 5 条审计事件。" : "当前账号无审计查看权限。"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentAudit.length === 0 ? (
              <EmptyState title="暂无业务动态" description="关键业务动作会同步写入审计日志。" />
            ) : (
              <div className="space-y-2">
                {recentAudit.map((event) => (
                  <div key={event.id} className="rounded-md border border-border px-3 py-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono">{event.action}</span>
                      <span className="text-muted-foreground">
                        {formatDateTime(event.occurredAt)}
                      </span>
                    </div>
                    <p className="mt-1 text-muted-foreground">
                      {event.objectLabel ?? event.objectId ?? "—"}
                    </p>
                  </div>
                ))}
                <Button asChild variant="outline" size="sm">
                  <Link to="/audit">
                    <History className="h-3.5 w-3.5" />
                    查看审计
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>业务入口</CardTitle>
          <CardDescription>按角色权限显示可用功能。</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to="/approvals">
              <ClipboardCheck className="h-3.5 w-3.5" />
              待办审批
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/assets">
              <Laptop className="h-3.5 w-3.5" />
              资产台账
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/licenses">
              <Tags className="h-3.5 w-3.5" />
              软件许可
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/maintenance">
              <Wrench className="h-3.5 w-3.5" />
              维修维保
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/reports">
              <FileBarChart className="h-3.5 w-3.5" />
              统计报表
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/reminders">
              <Bell className="h-3.5 w-3.5" />
              提醒中心
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/audit">
              <History className="h-3.5 w-3.5" />
              审计日志
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
